import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom'; // CRITICAL: Force immediate UI updates
// Use optimized Firestore subscriptions with onSnapshot for instant cache loading
import {
  subscribeToProducts,
  subscribeToProductsByCategory
} from '../firebase/firestoreOptimizedV2';

/**
 * Hook for fetching all products with real-time updates
 * Uses onSnapshot for instant cache loading and real-time sync
 * CRITICAL FIX: Start with loading=false to show cached data immediately
 */
export const useProducts = () => {
  const [products, setProducts] = useState([]);
  // CRITICAL FIX: Start with false loading - show cached data immediately
  // Only set loading=true if no cached data arrives after a short delay
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Don't set loading to true immediately - let cache load first
    setError(null);

    console.log('🔍 useProducts: Setting up subscription...');

    let hasReceivedData = false;

    // Subscribe to products - onSnapshot uses cache first, then syncs with server
    // This provides instant loading for returning users (persistentLocalCache)
    // CRITICAL: Use flushSync for immediate UI update - don't wait for React batching
    unsubscribeRef.current = subscribeToProducts((productsData) => {
      console.log('📦 useProducts: Received products data:', {
        count: productsData.length,
        firstProduct: productsData[0] ? { id: productsData[0].id, name: productsData[0].name } : null
      });

      if (mountedRef.current) {
        hasReceivedData = true;
        // Force immediate React update - don't wait for batching
        flushSync(() => {
          setProducts(productsData);
          setLoading(false);
          setError(null);
        });
      } else {
        console.warn('⚠️ useProducts: Component unmounted, ignoring data update');
      }
    });

    // Set loading true only after a short delay if no data yet
    // This allows cached data to show immediately
    const loadingTimeout = setTimeout(() => {
      if (mountedRef.current && !hasReceivedData) {
        setLoading(true);
      }
    }, 100);

    // Cleanup subscription on unmount
    return () => {
      clearTimeout(loadingTimeout);
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  return { products, loading, error };
};

/**
 * Hook for filtered products by category with real-time updates
 * Uses onSnapshot for instant cache loading and real-time sync
 * CRITICAL FIX: Start with loading=false to show cached data immediately
 */
export const useProductsByCategory = (category) => {
  const [filteredProducts, setFilteredProducts] = useState([]);
  // CRITICAL FIX: Start with false loading - show cached data immediately
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    if (!category) {
      setFilteredProducts([]);
      setLoading(false);
      return;
    }

    // Don't set loading to true immediately - let cache load first
    setError(null);

    let hasReceivedData = false;

    // Cleanup previous subscription if category changed
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Subscribe to products by category - onSnapshot uses cache first
    // CRITICAL: Use flushSync for immediate UI update - don't wait for React batching
    unsubscribeRef.current = subscribeToProductsByCategory(category, (productsData) => {
      if (mountedRef.current) {
        hasReceivedData = true;
        // Force immediate React update - don't wait for batching
        flushSync(() => {
          setFilteredProducts(productsData);
          setLoading(false);
          setError(null);
        });
      }
    });

    // Set loading true only after a short delay if no data yet
    // This allows cached data to show immediately
    const loadingTimeout = setTimeout(() => {
      if (mountedRef.current && !hasReceivedData) {
        setLoading(true);
      }
    }, 100);

    // Cleanup subscription on unmount or category change
    return () => {
      clearTimeout(loadingTimeout);
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [category]);

  return { products: filteredProducts, loading, error };
};

// Backward compatibility: clearProductsCache is no longer needed with real-time subscriptions
// Subscriptions automatically update when data changes, so this is a no-op
export const clearProductsCache = () => {
  // No-op: Real-time subscriptions handle updates automatically
  // This function is kept for backward compatibility with existing code
};
